import Image from 'next/image';

import { Quote } from 'lucide-react';

import type { MarketingLocale } from '~/lib/marketing/site';

import { BUILT_WITH_YOU } from '../../_lib/platform.content';
import { FitScale } from '../product-demos/demo-kit';
import { FEATURE_REQUEST_DEMO_SIZE } from '../product-demos/demo-sizes';
import { FeatureRequestDemo } from '../product-demos/feature-request-demo';

const IMAGE = '/landing-pages/flow/images/audiences/built-with-you.webp';
const FRAME_LOGO = '/landing-pages/flow/customers/frame-studio.png';

const IMAGE_ALT = {
  en: 'A studio owner and a product specialist reviewing a feature together at the studio front desk',
  ar: 'صاحبة استوديو ومختص منتج يراجعان ميزة معًا عند مكتب الاستقبال',
} as const;

/**
 * The customisation story: requests go straight to the product team and
 * ship weekly. Dark band so it reads as a distinct promise, not a feature.
 */
export function BuiltWithYouSection({ locale }: { locale: MarketingLocale }) {
  return (
    <section
      id="built-with-you"
      className="relative scroll-mt-16 overflow-hidden bg-[#202020] px-4 py-16 text-white md:px-8 md:py-24 lg:px-[90px] lg:py-[120px]"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 start-1/3 h-[600px] w-[600px] rounded-full bg-[#a9c8ff]/[0.07] blur-[140px]"
      />

      <div className="relative mx-auto max-w-[1320px]">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-16">
          <div>
            <div className="mb-4 flex items-center gap-3 lg:mb-6">
              <span className="h-2 w-2 rounded-full bg-[#a9c8ff]" aria-hidden="true" />
              <p className="font-roboto-mono text-[12px] uppercase tracking-wide text-[#a9c8ff] md:text-[14px]">
                {BUILT_WITH_YOU.label[locale]}
              </p>
            </div>
            <h2
              className="font-roboto text-[40px] leading-[1.02] tracking-tight md:text-[56px] lg:text-[72px]"
              style={{ fontVariationSettings: "'wdth' 100" }}
            >
              {BUILT_WITH_YOU.heading[locale]}
            </h2>
            <p className="mt-6 max-w-[560px] font-roboto text-[16px] leading-relaxed text-white/65 md:text-[18px]">
              {BUILT_WITH_YOU.description[locale]}
            </p>

            <dl className="mt-10 grid grid-cols-3 gap-4 border-t border-white/10 pt-8">
              {BUILT_WITH_YOU.stats.map((stat) => (
                <div key={stat.label.en} className="flex flex-col-reverse justify-end">
                  <dt className="mt-2 font-roboto text-[12px] leading-snug text-white/50 md:text-[13px]">
                    {stat.label[locale]}
                  </dt>
                  <dd className="font-roboto text-[26px] font-bold md:text-[36px]">
                    {stat.value[locale]}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <figure>
            <div className="rounded-[24px] border border-white/10 bg-white/[0.03] p-4 md:p-6">
              <FitScale
                width={FEATURE_REQUEST_DEMO_SIZE.width}
                height={FEATURE_REQUEST_DEMO_SIZE.height}
              >
                <FeatureRequestDemo locale={locale} />
              </FitScale>
            </div>
            <figcaption className="mt-3 text-center font-roboto-mono text-[11px] uppercase tracking-wide text-white/40">
              {BUILT_WITH_YOU.demoCaption[locale]}
            </figcaption>
          </figure>
        </div>

        <figure className="mx-auto mt-20 max-w-[960px] text-center md:mt-28">
          <Quote className="mx-auto h-8 w-8 text-[#a9c8ff]" aria-hidden="true" />
          <blockquote
            className="mt-6 font-roboto text-[24px] leading-[1.35] text-white md:text-[34px]"
            style={{ fontVariationSettings: "'wdth' 100" }}
          >
            {BUILT_WITH_YOU.quote[locale]}
          </blockquote>
          <figcaption className="mt-6 flex items-center justify-center gap-3">
            <Image
              src={FRAME_LOGO}
              alt=""
              width={72}
              height={32}
              className="h-7 w-auto object-contain"
            />
            <span className="font-roboto-mono text-[12px] uppercase tracking-wide text-white/55">
              {BUILT_WITH_YOU.quoteAttribution[locale]}
            </span>
          </figcaption>
        </figure>

        <div className="mt-20 grid grid-cols-1 items-center gap-10 md:mt-28 lg:grid-cols-2 lg:gap-16">
          <div className="relative aspect-[4/3] overflow-hidden rounded-[24px]">
            <Image
              src={IMAGE}
              alt={IMAGE_ALT[locale]}
              fill
              sizes="(max-width: 1024px) 100vw, 620px"
              className="object-cover"
            />
          </div>
          <div>
            <p className="mb-6 font-roboto-mono text-[12px] uppercase tracking-wide text-white/50">
              {BUILT_WITH_YOU.steps.label[locale]}
            </p>
            <ol className="relative space-y-7 before:absolute before:bottom-3 before:start-[17px] before:top-3 before:w-px before:bg-white/15">
              {BUILT_WITH_YOU.steps.items.map((step, index) => (
                <li key={step.title.en} className="relative flex gap-5">
                  <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/20 bg-[#202020] font-roboto-mono text-[12px] text-[#a9c8ff]">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div className="pt-1.5">
                    <h3 className="font-roboto text-[18px] font-medium text-white md:text-[20px]">
                      {step.title[locale]}
                    </h3>
                    <p className="mt-1.5 font-roboto text-[15px] leading-relaxed text-white/60">
                      {step.body[locale]}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}
